import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  InputNumber,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import {
  useGetVariantRecipeConfigQuery,
  useUpdateVariantRecipeConfigMutation,
} from '../../services/recipesApi';
import { useGetInventoryItemsQuery } from '../../services/inventoryApi';
import { inventoryItemLabel, RECIPE_MIN_QUANTITY } from './RecipeIngredientsEditor';
import type {
  UpdateRecipeVariantRuleRequest,
  UpdateVariantRecipeConfigRequest,
  VariantRecipeConfig,
} from '../../types/inventory';
import { compatibleUnits } from '../../utils/unitHelpers';
import { getErrorMessage } from '../../utils/errorHelpers';

const { Text } = Typography;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const SELECTION_MODE_LABELS: Record<string, string> = {
  SingleSelect: 'Selección única',
  MultiSelect: 'Selección múltiple',
  QuantitySplit: 'Cantidad dividida',
};

interface ItemRow {
  key: string;
  inventoryItemId?: string;
  quantity?: number;
  unit?: string;
}

interface RuleRow {
  key: string;
  whenVariantId?: string;
  // 'g:<groupId>' (grupo de modificadores) | 'i:<inventoryItemId>' (ingrediente base)
  target?: string;
  quantity?: number;
  // La cantidad de la regla lleva SU PROPIA unidad: las líneas del grupo
  // objetivo pueden estar guardadas en unidades distintas (0.2 L vs 200 ml).
  unit?: string;
}

interface VariantRecipeConfigModalProps {
  open: boolean;
  productId: string | null;
  productName: string;
  onClose: () => void;
  isMobile: boolean;
}

const nextKey = () => crypto.randomUUID();

const VariantRecipeConfigModal: React.FC<VariantRecipeConfigModalProps> = ({
  open,
  productId,
  productName,
  onClose,
  isMobile,
}) => {
  const { message } = App.useApp();

  const { data: config, isFetching } = useGetVariantRecipeConfigQuery(productId!, {
    skip: !productId || !open,
  });
  const { data: inventoryItems } = useGetInventoryItemsQuery();
  const [updateConfig, { isLoading: saving }] = useUpdateVariantRecipeConfigMutation();

  const [itemsByVariant, setItemsByVariant] = useState<Record<string, ItemRow[]>>({});
  const [rules, setRules] = useState<RuleRow[]>([]);

  useEffect(() => {
    if (!config || !open) return;
    const initial: Record<string, ItemRow[]> = {};
    config.groups.forEach((group) =>
      group.variants.forEach((variant) => {
        initial[variant.variantId] = variant.items.map((item) => ({
          key: nextKey(),
          inventoryItemId: item.inventoryItemId,
          quantity: item.quantity,
          unit: item.unit,
        }));
      })
    );
    setItemsByVariant(initial);
    setRules(
      config.rules.map((rule) => ({
        key: nextKey(),
        whenVariantId: rule.whenVariantId,
        target: rule.targetVariantGroupId
          ? `g:${rule.targetVariantGroupId}`
          : `i:${rule.targetInventoryItemId}`,
        quantity: rule.quantity,
        unit: rule.unit,
      }))
    );
  }, [config, open]);

  const stockUnitOf = (inventoryItemId?: string) =>
    inventoryItems?.find((i) => i.id === inventoryItemId)?.unit;

  const inventoryOptions = useMemo(
    () =>
      inventoryItems?.map((item) => ({
        value: item.id,
        label: inventoryItemLabel(item),
      })) ?? [],
    [inventoryItems]
  );

  const variantOptions = useMemo(
    () =>
      (config?.groups ?? []).map((group) => ({
        label: group.groupName,
        options: group.variants.map((variant) => ({
          value: variant.variantId,
          label: variant.variantName,
        })),
      })),
    [config]
  );

  const targetOptions = useMemo(() => {
    if (!config) return [];
    const groupTargets = config.groups
      .filter((group) => group.groupId !== EMPTY_GUID)
      .map((group) => ({
        value: `g:${group.groupId}`,
        label: `Lo que se elija de "${group.groupName}"`,
      }));
    const seen = new Set<string>();
    const baseTargets = config.baseItems
      .filter((item) => !seen.has(item.inventoryItemId) && seen.add(item.inventoryItemId))
      .map((item) => ({
        value: `i:${item.inventoryItemId}`,
        label: `Ingrediente base: ${item.itemName}`,
      }));
    // Una regla sobrevive a quitar su ingrediente de la receta base. Sin esta opción se vería el
    // id crudo, y es lo que hay que eliminar para poder borrar ese insumo.
    const orphanTargets = rules
      .map((rule) => rule.target)
      .filter((target): target is string => !!target?.startsWith('i:') && !seen.has(target.slice(2)))
      .filter((target, index, all) => all.indexOf(target) === index)
      .map((target) => ({
        value: target,
        label: `Ya no está en la receta: ${
          inventoryItems?.find((i) => i.id === target.slice(2))?.name ?? 'insumo'
        }`,
      }));
    return [
      { label: 'Grupos de modificadores', options: groupTargets },
      { label: 'Receta base', options: [...baseTargets, ...orphanTargets] },
    ];
  }, [config, rules, inventoryItems]);

  // Unidades válidas para la cantidad de una regla, según su objetivo:
  // ingrediente base → compatibles con su unidad de stock; grupo → unión de
  // las compatibles de los insumos configurados en ese grupo.
  const unitOptionsForTarget = (target?: string): string[] => {
    if (!config || !target) return [];
    if (target.startsWith('i:')) {
      const stock = stockUnitOf(target.slice(2));
      return stock ? compatibleUnits(stock) : [];
    }
    const group = config.groups.find((g) => g.groupId === target.slice(2));
    const units = new Set<string>();
    group?.variants.forEach((variant) =>
      (itemsByVariant[variant.variantId] ?? []).forEach((row) => {
        const stock = stockUnitOf(row.inventoryItemId);
        if (stock) compatibleUnits(stock).forEach((u) => units.add(u));
      })
    );
    return units.size > 0 ? Array.from(units) : ['g', 'kg', 'ml', 'L', 'pz'];
  };

  const updateItemRow = (variantId: string, key: string, patch: Partial<ItemRow>) => {
    setItemsByVariant((prev) => ({
      ...prev,
      [variantId]: (prev[variantId] ?? []).map((row) =>
        row.key === key ? { ...row, ...patch } : row
      ),
    }));
  };

  const handleItemChange = (variantId: string, key: string, inventoryItemId: string) => {
    // La unidad de la línea se ancla a la unidad de stock del insumo para
    // que descuento y costo nunca multipliquen magnitudes incompatibles.
    updateItemRow(variantId, key, { inventoryItemId, unit: stockUnitOf(inventoryItemId) });
  };

  const buildPayload = (cfg: VariantRecipeConfig): UpdateVariantRecipeConfigRequest => {
    const variants = cfg.groups.flatMap((group) =>
      group.variants.map((variant) => {
        const rows = itemsByVariant[variant.variantId] ?? [];
        const incomplete = rows.some((r) => !r.inventoryItemId || !r.quantity || !r.unit);
        if (incomplete) {
          throw new Error(`Completa los insumos del modificador "${variant.variantName}" o elimínalos`);
        }
        return {
          variantId: variant.variantId,
          items: rows.map((r) => ({
            inventoryItemId: r.inventoryItemId!,
            quantity: r.quantity!,
            unit: r.unit!,
          })),
        };
      })
    );

    const ruleRequests: UpdateRecipeVariantRuleRequest[] = rules.map((rule, index) => {
      if (!rule.whenVariantId || !rule.target || !rule.quantity || !rule.unit) {
        throw new Error(`Completa la regla #${index + 1} (incluida su unidad) o elimínala`);
      }
      return {
        whenVariantId: rule.whenVariantId,
        targetVariantGroupId: rule.target.startsWith('g:') ? rule.target.slice(2) : undefined,
        targetInventoryItemId: rule.target.startsWith('i:') ? rule.target.slice(2) : undefined,
        quantity: rule.quantity,
        unit: rule.unit,
      };
    });

    return { variants, rules: ruleRequests };
  };

  const handleSave = async () => {
    if (!config || !productId) return;

    let payload: UpdateVariantRecipeConfigRequest;
    try {
      payload = buildPayload(config);
    } catch (error) {
      message.warning(error instanceof Error ? error.message : String(error));
      return;
    }

    try {
      await updateConfig({ productId, data: payload }).unwrap();
      message.success('Configuración de modificadores guardada');
      onClose();
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  const renderConfig = (cfg: VariantRecipeConfig) => (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Cada modificador puede consumir sus propios insumos del inventario. Las reglas ajustan cantidades según lo elegido (ej: Frío usa 300 ml de la leche seleccionada en lugar de 200 ml)."
      />

      {!cfg.hasBaseRecipe && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Este producto no tiene receta base. Los modificadores descontarán inventario, pero los ingredientes fijos del producto no."
        />
      )}

      {cfg.groups.map((group) => (
        <Card
          key={group.groupId}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <span>
              {group.groupName}{' '}
              <Tag>{SELECTION_MODE_LABELS[group.selectionMode] ?? group.selectionMode}</Tag>
            </span>
          }
        >
          {group.variants.map((variant) => {
            const rows = itemsByVariant[variant.variantId] ?? [];
            return (
              <div key={variant.variantId} style={{ marginBottom: 12 }}>
                <Text strong>{variant.variantName}</Text>
                {rows.map((row) => {
                  const stockUnit = stockUnitOf(row.inventoryItemId);
                  return (
                    <div
                      key={row.key}
                      style={{
                        display: 'flex',
                        gap: 8,
                        marginTop: 6,
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                      }}
                    >
                      <Select
                        showSearch
                        placeholder="Insumo"
                        optionFilterProp="label"
                        value={row.inventoryItemId}
                        onChange={(value) => handleItemChange(variant.variantId, row.key, value)}
                        options={inventoryOptions}
                        style={{ flex: isMobile ? '1 1 100%' : '1 1 45%' }}
                      />
                      <InputNumber
                        placeholder="Cantidad"
                        min={RECIPE_MIN_QUANTITY}
                        step={0.1}
                        value={row.quantity}
                        onChange={(value) =>
                          updateItemRow(variant.variantId, row.key, { quantity: value ?? undefined })
                        }
                        style={{ flex: isMobile ? '1 1 40%' : '0 0 110px' }}
                      />
                      <Select
                        placeholder="Unidad"
                        value={row.unit}
                        onChange={(value) => updateItemRow(variant.variantId, row.key, { unit: value })}
                        options={(stockUnit ? compatibleUnits(stockUnit) : []).map((u) => ({
                          value: u,
                          label: u,
                        }))}
                        disabled={!row.inventoryItemId}
                        style={{ flex: isMobile ? '1 1 40%' : '0 0 90px' }}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() =>
                          setItemsByVariant((prev) => ({
                            ...prev,
                            [variant.variantId]: rows.filter((r) => r.key !== row.key),
                          }))
                        }
                      />
                    </div>
                  );
                })}
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  style={{ paddingLeft: 0, marginTop: 4 }}
                  onClick={() =>
                    setItemsByVariant((prev) => ({
                      ...prev,
                      [variant.variantId]: [...rows, { key: nextKey() }],
                    }))
                  }
                >
                  Agregar insumo
                </Button>
              </div>
            );
          })}
        </Card>
      ))}

      <Card size="small" title="Reglas de cantidad" style={{ marginBottom: 4 }}>
        {rules.length === 0 && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Sin reglas: cada modificador usa siempre su cantidad base.
          </Text>
        )}
        {rules.map((rule) => (
          <div
            key={rule.key}
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 8,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              alignItems: 'center',
            }}
          >
            <Text type="secondary">Cuando</Text>
            <Select
              placeholder="Modificador"
              value={rule.whenVariantId}
              onChange={(value) =>
                setRules((prev) =>
                  prev.map((r) => (r.key === rule.key ? { ...r, whenVariantId: value } : r))
                )
              }
              options={variantOptions}
              style={{ flex: isMobile ? '1 1 100%' : '0 0 170px' }}
            />
            <Select
              placeholder="Objetivo"
              value={rule.target}
              onChange={(value) =>
                setRules((prev) =>
                  prev.map((r) =>
                    r.key === rule.key
                      ? { ...r, target: value, unit: unitOptionsForTarget(value)[0] }
                      : r
                  )
                )
              }
              options={targetOptions}
              style={{ flex: isMobile ? '1 1 100%' : '1 1 40%' }}
            />
            <Text type="secondary">usa</Text>
            <InputNumber
              placeholder="Cantidad"
              min={RECIPE_MIN_QUANTITY}
              step={0.1}
              value={rule.quantity}
              onChange={(value) =>
                setRules((prev) =>
                  prev.map((r) => (r.key === rule.key ? { ...r, quantity: value ?? undefined } : r))
                )
              }
              style={{ flex: isMobile ? '1 1 40%' : '0 0 100px' }}
            />
            <Select
              placeholder="Unidad"
              value={rule.unit}
              onChange={(value) =>
                setRules((prev) => prev.map((r) => (r.key === rule.key ? { ...r, unit: value } : r)))
              }
              options={unitOptionsForTarget(rule.target).map((u) => ({ value: u, label: u }))}
              disabled={!rule.target}
              style={{ flex: isMobile ? '1 1 40%' : '0 0 85px' }}
            />
            <Button
              type="text"
              danger
              icon={<MinusCircleOutlined />}
              onClick={() => setRules((prev) => prev.filter((r) => r.key !== rule.key))}
            />
          </div>
        ))}
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={() => setRules((prev) => [...prev, { key: nextKey() }])}
        >
          Agregar regla
        </Button>
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          La cantidad de la regla se expresa en la unidad que elijas aquí, sin importar
          cómo esté capturada la línea del insumo.
        </Text>
      </Card>
    </>
  );

  return (
    <Modal
      title={`Modificadores e inventario — ${productName}`}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={saving}
      width={isMobile ? '95vw' : 860}
      destroyOnClose
    >
      {isFetching || !config ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : config.groups.length === 0 ? (
        <Empty description="Este producto no tiene grupos de modificadores. Configúralos primero desde Menú → Modificadores." />
      ) : (
        renderConfig(config)
      )}
    </Modal>
  );
};

export default VariantRecipeConfigModal;
